import { useEffect, useState } from 'react';
import DashboardLayout from '../../components/layout/DashboardLayout';
import api, { getApiError } from '../../lib/axios';
import { Activity, CheckCircle, XCircle, AlertTriangle, Clock, Loader2, Play, ChevronLeft, ChevronRight, RefreshCw } from 'lucide-react';

interface CheckSummary {
  total_domains: number;
  total_checked: number;
  unchecked: number;
  alive: number;
  dead: number;
  timeout: number;
  redirect: number;
  error: number;
  last_check: string | null;
}

interface CheckResult {
  id: string;
  domain_id: string;
  domain_name: string | null;
  status: string;
  http_code: number | null;
  response_time_ms: number | null;
  redirect_url: string | null;
  error_message: string | null;
  checked_at: string | null;
}

export default function AdminMonitor() {
  const [summary, setSummary] = useState<CheckSummary | null>(null);
  const [results, setResults] = useState<CheckResult[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [statusFilter, setStatusFilter] = useState('');
  const [loading, setLoading] = useState(true);
  const [checking, setChecking] = useState(false);
  const [error, setError] = useState('');
  const perPage = 50;

  const fetchSummary = async () => {
    try {
      const res = await api.get('/api/monitor/summary');
      setSummary(res.data);
    } catch (err: any) {
      setError(getApiError(err));
    }
  };

  const fetchResults = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page: String(page), per_page: String(perPage) });
      if (statusFilter) params.set('status_filter', statusFilter);
      const res = await api.get(`/api/monitor/results?${params}`);
      setResults(res.data.results);
      setTotal(res.data.total);
    } catch (err: any) {
      setError(getApiError(err));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchSummary(); }, []);
  useEffect(() => { fetchResults(); }, [page, statusFilter]);

  const triggerCheck = async (limit: number) => {
    setChecking(true);
    setError('');
    try {
      const res = await api.post('/api/monitor/check', { limit });
      setError(''); // clear any previous error
      alert(`Health check started for ${res.data.checking} domains. Refresh in a minute to see results.`);
      // Refresh summary after a delay
      setTimeout(() => { fetchSummary(); fetchResults(); }, 5000);
    } catch (err: any) {
      setError(getApiError(err, 'Failed to start check'));
    } finally {
      setChecking(false);
    }
  };

  const statusIcon = (status: string) => {
    switch (status) {
      case 'alive': return <CheckCircle className="w-4 h-4 text-green-500" />;
      case 'dead': return <XCircle className="w-4 h-4 text-red-500" />;
      case 'timeout': return <Clock className="w-4 h-4 text-yellow-500" />;
      case 'redirect': return <RefreshCw className="w-4 h-4 text-blue-500" />;
      default: return <AlertTriangle className="w-4 h-4 text-orange-500" />;
    }
  };

  const statusBadge = (status: string) => {
    const styles: Record<string, string> = {
      alive: 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400',
      dead: 'bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400',
      timeout: 'bg-yellow-100 dark:bg-yellow-900/30 text-yellow-700 dark:text-yellow-400',
      redirect: 'bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400',
      error: 'bg-orange-100 dark:bg-orange-900/30 text-orange-700 dark:text-orange-400',
    };
    return (
      <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium capitalize ${styles[status] || ''}`}>
        {statusIcon(status)} {status}
      </span>
    );
  };

  const totalPages = Math.ceil(total / perPage);

  return (
    <DashboardLayout>
      <div className="space-y-6">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Link Monitor</h1>
            <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
              Domain health checking &amp; monitoring
            </p>
          </div>
          <div className="flex gap-2">
            <button
              onClick={() => triggerCheck(100)}
              disabled={checking}
              className="px-4 py-2 bg-brand-600 hover:bg-brand-700 text-white text-sm font-medium rounded-lg flex items-center gap-2 disabled:opacity-50"
            >
              {checking ? <Loader2 className="w-4 h-4 animate-spin" /> : <Play className="w-4 h-4" />}
              Check 100 Domains
            </button>
            <button
              onClick={() => triggerCheck(500)}
              disabled={checking}
              className="px-4 py-2 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-300 text-sm font-medium rounded-lg flex items-center gap-2 disabled:opacity-50"
            >
              Check 500
            </button>
          </div>
        </div>

        {error && (
          <div className="p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg text-sm text-red-700 dark:text-red-400">
            {error}
          </div>
        )}

        {/* Summary Cards */}
        {summary && (
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-3">
            {[
              { label: 'Total', value: summary.total_domains, color: 'text-gray-900 dark:text-white' },
              { label: 'Checked', value: summary.total_checked, color: 'text-blue-600' },
              { label: 'Unchecked', value: summary.unchecked, color: 'text-gray-500' },
              { label: 'Alive', value: summary.alive, color: 'text-green-600' },
              { label: 'Dead', value: summary.dead, color: 'text-red-600' },
              { label: 'Timeout', value: summary.timeout, color: 'text-yellow-600' },
              { label: 'Error', value: summary.error, color: 'text-orange-600' },
            ].map(card => (
              <div key={card.label} className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-3 text-center">
                <p className={`text-xl font-bold ${card.color}`}>{card.value.toLocaleString()}</p>
                <p className="text-xs text-gray-500 dark:text-gray-400">{card.label}</p>
              </div>
            ))}
          </div>
        )}

        {summary?.last_check && (
          <p className="text-xs text-gray-400">
            Last check: {new Date(summary.last_check).toLocaleString()}
          </p>
        )}

        {/* Filter Tabs */}
        <div className="flex gap-2 flex-wrap">
          {['', 'alive', 'dead', 'timeout', 'redirect', 'error'].map(f => (
            <button
              key={f}
              onClick={() => { setStatusFilter(f); setPage(1); }}
              className={`px-3 py-1.5 text-sm rounded-lg font-medium ${
                statusFilter === f
                  ? 'bg-brand-50 dark:bg-brand-900/20 text-brand-600 dark:text-brand-400'
                  : 'text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700'
              }`}
            >
              {f ? f.charAt(0).toUpperCase() + f.slice(1) : 'All'}
            </button>
          ))}
        </div>

        {/* Results Table */}
        {loading ? (
          <div className="flex justify-center py-12">
            <Loader2 className="w-8 h-8 animate-spin text-brand-600" />
          </div>
        ) : results.length === 0 ? (
          <div className="text-center py-12 bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700">
            <Activity className="w-12 h-12 text-gray-300 dark:text-gray-600 mx-auto mb-3" />
            <p className="text-gray-500 dark:text-gray-400">No check results yet</p>
            <p className="text-xs text-gray-400 mt-1">Click "Check 100 Domains" to start</p>
          </div>
        ) : (
          <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-gray-50 dark:bg-gray-700/50 border-b border-gray-200 dark:border-gray-700">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Domain</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">HTTP</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Response</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Error</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Checked</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                  {results.map(r => (
                    <tr key={r.id} className="hover:bg-gray-50 dark:hover:bg-gray-700/30">
                      <td className="px-4 py-3 font-medium text-gray-900 dark:text-white">{r.domain_name || r.domain_id}</td>
                      <td className="px-4 py-3">{statusBadge(r.status)}</td>
                      <td className="px-4 py-3 text-gray-700 dark:text-gray-300">{r.http_code || '—'}</td>
                      <td className="px-4 py-3 text-gray-700 dark:text-gray-300">
                        {r.response_time_ms ? `${r.response_time_ms}ms` : '—'}
                      </td>
                      <td className="px-4 py-3 text-gray-500 text-xs max-w-48 truncate">{r.error_message || '—'}</td>
                      <td className="px-4 py-3 text-gray-500 text-xs">
                        {r.checked_at ? new Date(r.checked_at).toLocaleString() : '—'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {totalPages > 1 && (
              <div className="px-4 py-3 border-t border-gray-200 dark:border-gray-700 flex items-center justify-between">
                <p className="text-sm text-gray-500">Page {page} of {totalPages} ({total} results)</p>
                <div className="flex gap-2">
                  <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page <= 1} className="p-2 rounded-lg border border-gray-300 dark:border-gray-600 hover:bg-gray-100 dark:hover:bg-gray-700 disabled:opacity-50">
                    <ChevronLeft className="w-4 h-4" />
                  </button>
                  <button onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page >= totalPages} className="p-2 rounded-lg border border-gray-300 dark:border-gray-600 hover:bg-gray-100 dark:hover:bg-gray-700 disabled:opacity-50">
                    <ChevronRight className="w-4 h-4" />
                  </button>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </DashboardLayout>
  );
}
